'use client'

import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  InMemoryCache,
} from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import { setContext } from '@apollo/client/link/context'
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs'
import { Auth } from 'aws-amplify'
import { useSession } from 'next-auth/react'
import { FC, ReactNode, useMemo } from 'react'
import {
  Comment,
  CommentEdge,
  Reply,
  ReplyEdge,
  Video,
  VideoRecommendsEdge,
} from '~/__generated__/graphql'
import { getLanguageLocalStorage } from '~/features/i18n/utils/languageLocalStorage'
import { getAnonymousUserId } from '../anonymousUserId'
import browserEnv from '../env/browserEnv'
import { getClientId } from '../ga'
import { cursorConnection } from './cursorConnection'

const uploadLink = createUploadLink({
  uri: `${browserEnv.NEXT_PUBLIC_API_HOST}/graphql`,
})

const ApolloProviderWrapper: FC<{ children?: ReactNode }> = ({ children }) => {
  const localStorageLanguage = getLanguageLocalStorage()
  const { data: snsSession } = useSession()

  const client = useMemo(() => {
    const authLink = setContext(async (_, { headers, ...rest }) => {
      let token: string | undefined
      if (snsSession) {
        token = snsSession.access_token
      } else {
        try {
          const session = await Auth.currentSession()
          token = session.getIdToken().getJwtToken()
        } catch (e) {}
      }

      return {
        ...rest,
        headers: {
          ...headers,
          ...(token !== undefined && { Authorization: `Bearer ${token}` }),
          'X-App-Language': localStorageLanguage ?? 'en',
          'X-Anonymous-User-Id': getAnonymousUserId(),
          'X-Ga-Client-Id': getClientId() ?? '',
        },
      }
    })
    const errorLink = onError(({ networkError }) => {
      if (
        networkError &&
        'statusCode' in networkError &&
        networkError.statusCode >= 500
      ) {
        if (networkError.statusCode === 503) {
          window.location.href = '/maintenance'
        } else if (window.location.pathname !== '/server_error') {
          window.location.href = '/server_error'
        }
      }
    })

    return new ApolloClient({
      link: ApolloLink.from([errorLink, authLink, uploadLink]),
      connectToDevTools: process.env.NODE_ENV !== 'production',
      cache: new InMemoryCache({
        typePolicies: {
          VideoCommentsConnection: cursorConnection<CommentEdge, Comment>(),
          CommentRepliesConnection: cursorConnection<ReplyEdge, Reply>(),
          VideoRecommendsConnection: cursorConnection<VideoRecommendsEdge, Video>(),
        },
      }),
    })
  }, [snsSession])

  return <ApolloProvider client={client}>{children}</ApolloProvider>
}

export default ApolloProviderWrapper
